/**
 * The **provider connection editor** — the ingress fork of the shared connection
 * form pattern (``shared/form-patterns.md`` §1; ADR-040 §D-1; Flow V Frame 5).
 *
 * Edits one connection belonging to a provider interface: role, endpoint, port,
 * timeout, bounded retry, per-connection state. An interface may hold several,
 * so this renders one card per connection with add / remove above it.
 *
 * # The direction vocabulary is forked, not shared
 *
 * ``listen`` / ``dial_out`` rather than egress's ``pull`` / ``dial_out``. Data
 * flows provider → Meridian under **both** values; the role names who initiates
 * the transport connection. The roles a direction can realize differ, and a
 * shared name whose value set flips per direction would be worse than two names
 * (ADR-040 V-2).
 *
 * # Three behaviours worth naming
 *
 * **Switching role rewrites the endpoint; it does not merely relabel it.** A
 * bind address and a remote host are different facts about different machines,
 * and retaining one under the other's label is how an operator publishes an
 * internal address or dials their own listener. Note this clears in **both**
 * directions, unlike the egress twin: there ``pull`` forbids a host outright, so
 * only one direction needed clearing; here ``listen`` legitimately *accepts* an
 * optional bind address, which is precisely what makes a stale remote host
 * survivable and dangerous.
 *
 * **Retry is bounded, and the form says why.** An endlessly-retried dial is a
 * wedged interface, not resilience. ⚠ Retry *and* timeout are **carried but
 * not yet honoured** (GAP-58, architect ruling 2026-09-28): no ingress transport
 * reads either one. The form keeps them — they are persisted, valid, and become
 * live the day a transport honours them — but says so inline, because a control
 * that accepts a value and discards its effect is the operator-visibility failure
 * CLAUDE.md §8.1 names.
 *
 * **Start/stop is not part of the form**, and **Delete is worded apart from it**
 * (REQ-UI-159). *Stop* silences this connection and keeps it; *Delete…* removes
 * it and it does not come back. Delete confirms; stop does not. Two controls
 * that read alike is how an operator reaching for a kill switch decommissions a
 * path instead.
 *
 * Cross-references:
 *   - ADR-040 §D-1 (the three-level model), §2.5 (roles); REQ-PROVIDER-007 / -016.
 *   - GAP-58 (the carried-but-unread `retry` / `timeout_seconds` pair).
 *   - REQ-UI-159 (per-connection delete, distinct from stop).
 *   - Flow V Frame 5; ``shared/form-patterns.md`` §1 and its §3 settlement.
 */
import { useState } from "react";
import {
  describePublishedRange,
  describeUnpublishedPort,
  portOutsidePublishedRange,
  type PublishedPortRange,
} from "@/lib/listen-port-range";
import { PeerNumberInput, PeerSelect, PeerTextInput } from "@/peers/peer-form-primitives";
import { StatePill, StateToggleButton } from "@/peers/peer-state-controls";
import { PeerSuppressionNote } from "@/peers/peer-suppression-note";
import type { EffectiveState } from "@/peers/peer-effective-state";
import {
  PROVIDER_CONNECTION_ROLES,
  findProviderConnectionRole,
  type ProviderConnectionRole,
} from "@/providers/provider-catalog";
import type { ProviderInterfaceConnectionConfig } from "@/providers/provider-model";

/** Props for {@link ProviderConnectionEditor}. */
export interface ProviderConnectionEditorProps {
  connection: ProviderInterfaceConnectionConfig;
  /** Index within the interface, for stable test ids and headings. */
  index: number;
  onChange: (next: ProviderInterfaceConnectionConfig) => void;
  /**
   * Drop this connection from the **draft**. Applied by the next Save, undone by
   * Cancel — so it needs no confirmation.
   */
  onRemove: () => void;
  /**
   * Delete this connection **on the server**, immediately (REQ-UI-159). Supplied
   * only for a connection that is already persisted; a draft-only row has
   * nothing to delete and gets {@link onRemove} instead.
   */
  onDelete?: () => void;
  /** Flip this connection's state through its own endpoint (not the Save button). */
  onToggleState?: (next: "started" | "stopped") => void;
  /** True while a start/stop for this connection is in flight. */
  statePending?: boolean;
  /** Validation message for the connection's name (duplicate within the interface). */
  nameError?: string | null;
  /** True while the interface has never been saved — state control is not yet meaningful. */
  unsaved?: boolean;
  /**
   * This connection resolved against its provider and interface (Flow V §5a).
   *
   * Rendered beside the state pill, never instead of it: a `started` connection
   * under a stopped interface is collecting nothing, and the pill alone would
   * say it is.
   */
  suppression?: EffectiveState;
  /**
   * The provider listener range the deployment publishes to the host
   * (REQ-DEPLOY-054), from ``GET /api/config``. A ``listen`` port outside it
   * is refused inline with the BFF's own wording (REQ-UI-198); ``null`` /
   * ``undefined`` means the range is not advertised, so nothing is refused
   * here and the hint says so. Irrelevant to ``dial_out``, whose port is the
   * provider's.
   */
  publishedRange?: PublishedPortRange | null;
}

/** Render the editor card for one provider connection. */
export function ProviderConnectionEditor({
  connection,
  index,
  onChange,
  onRemove,
  onDelete,
  onToggleState,
  statePending = false,
  nameError,
  unsaved = false,
  suppression,
  publishedRange,
}: ProviderConnectionEditorProps): React.ReactElement {
  // Held per connection rather than lifted: two open confirmations on one
  // interface would be two armed deletes with no way to tell which is which.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dialOut = connection.role === "dial_out";
  const role = findProviderConnectionRole(connection.role);
  // A local bind must sit inside the published range; the BFF refuses it on
  // save regardless of the connection's state, so the check here does not
  // read the state either — a stopped listener outside the range is still a
  // save the server will refuse (REQ-UI-198; REQ-PROVIDER-057).
  const portError =
    !dialOut &&
    publishedRange != null &&
    portOutsidePublishedRange(connection.endpoint_port, publishedRange)
      ? describeUnpublishedPort(connection.endpoint_port, publishedRange, "provider")
      : null;
  const set = (patch: Partial<ProviderInterfaceConnectionConfig>): void =>
    onChange({ ...connection, ...patch });

  const setRole = (value: string): void => {
    const next = value as ProviderConnectionRole;
    if (next === connection.role) return;
    // Cleared in BOTH directions — see the module docstring. `listen`'s
    // endpoint_host is an optional local bind address and `dial_out`'s is a
    // required remote host; carrying either across would leave a value that is
    // syntactically fine and describes the wrong machine.
    set({ role: next, endpoint_host: null });
  };

  return (
    <section
      data-testid={`provider-connection-${index}`}
      aria-label={connection.name === "" ? `Connection ${index + 1}` : connection.name}
      className="rounded border border-border bg-bg-base p-3"
    >
      <header className="flex flex-wrap items-center gap-2 pb-2">
        <h4 className="text-body-sm font-medium text-text-primary">
          {connection.name === "" ? `Connection ${index + 1}` : connection.name}
        </h4>
        <StatePill state={connection.state} />
        {suppression && (
          <PeerSuppressionNote resolved={suppression} testId={`provider-connection-${index}`} />
        )}
        <div className="ml-auto flex items-center gap-1">
          {onToggleState && !unsaved && (
            <StateToggleButton
              state={connection.state}
              pending={statePending}
              onToggle={onToggleState}
              testId={`provider-connection-${index}-toggle`}
              label="connection"
            />
          )}
          {onDelete === undefined ? (
            // Draft-only: nothing on the server to delete, and Cancel undoes it.
            <button
              type="button"
              onClick={onRemove}
              data-testid={`provider-connection-${index}-remove`}
              className="rounded border border-border px-2 py-0.5 text-meta text-text-secondary hover:border-destructive hover:text-destructive"
            >
              Remove
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              data-testid={`provider-connection-${index}-delete`}
              className="rounded border border-destructive px-2 py-0.5 text-meta text-destructive hover:bg-destructive/10"
            >
              Delete&hellip;
            </button>
          )}
        </div>
      </header>

      {/* The confirmation NAMES what it does and what it does not (REQ-UI-159).
          "This is not the same as stopping it" is the load-bearing clause: the
          two controls sit side by side, and an operator who conflated them would
          decommission a path expecting to silence it. The ingress
          non-implication is sharper than the egress one — there is no queue
          behind a removed listener, so what a provider sends meanwhile is gone. */}
      {confirmDelete && onDelete !== undefined && (
        <div
          role="alert"
          data-testid={`provider-connection-${index}-delete-confirm`}
          className="mb-2 flex flex-wrap items-center gap-2 rounded border border-destructive px-2 py-1 text-meta text-destructive"
        >
          <span className="flex-1">
            Delete connection <strong>{connection.name}</strong> permanently? Data a provider sends
            while this is gone is not queued. This is not the same as stopping it.
          </span>
          <button
            type="button"
            onClick={() => {
              setConfirmDelete(false);
              onDelete();
            }}
            data-testid={`provider-connection-${index}-delete-go`}
            className="rounded bg-destructive px-2 py-0.5 text-destructive-foreground hover:opacity-90"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(false)}
            data-testid={`provider-connection-${index}-delete-cancel`}
            className="rounded border border-border px-2 py-0.5 text-text-primary hover:bg-bg-elevated"
          >
            Keep
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <PeerTextInput
          label="Name"
          value={connection.name}
          maxLength={128}
          onChange={(name) => set({ name })}
          error={nameError}
          testId={`provider-connection-${index}-name`}
          placeholder="black-diamond-primary"
        />
        <PeerSelect
          label="Role"
          value={connection.role}
          testId={`provider-connection-${index}-role`}
          options={PROVIDER_CONNECTION_ROLES.map((r) => ({ value: r.value, label: r.label }))}
          onChange={setRole}
          hint={role?.hint}
        />
        {dialOut ? (
          <PeerTextInput
            label="Provider host"
            value={connection.endpoint_host ?? ""}
            mono
            onChange={(endpoint_host) => set({ endpoint_host })}
            testId={`provider-connection-${index}-host`}
            placeholder="sensor-gw.range.example"
            hint="Required when Meridian dials the provider."
          />
        ) : (
          <PeerTextInput
            label="Local bind address"
            value={connection.endpoint_host ?? ""}
            mono
            onChange={(endpoint_host) => set({ endpoint_host })}
            testId={`provider-connection-${index}-host`}
            placeholder="(every interface)"
            // Stated rather than left blank: an empty bind address is a real,
            // common and correct configuration, and an operator who reads the
            // blank as "unset" may narrow a listener that did not need it.
            hint="Optional. Leave blank to accept connections on every interface of this host."
          />
        )}
        <PeerNumberInput
          label={dialOut ? "Provider port" : "Local bind port"}
          value={connection.endpoint_port}
          min={1}
          max={65535}
          testId={`provider-connection-${index}-port`}
          onChange={(endpoint_port) => set({ endpoint_port })}
          error={portError}
          hint={
            dialOut
              ? "The port on the provider's host."
              : `The port on this host. ${describePublishedRange(publishedRange, "provider")}`
          }
        />
        <PeerNumberInput
          label="Timeout (seconds)"
          value={connection.timeout_seconds}
          min={0.1}
          max={3600}
          step="any"
          testId={`provider-connection-${index}-timeout`}
          onChange={(timeout_seconds) => set({ timeout_seconds })}
          hint="Connect and read timeout."
        />
        <PeerNumberInput
          label="Retry attempts"
          value={connection.retry?.max_attempts ?? 3}
          min={1}
          max={100}
          testId={`provider-connection-${index}-retry-attempts`}
          onChange={(max_attempts) =>
            set({
              retry: { max_attempts, backoff_seconds: connection.retry?.backoff_seconds ?? 1 },
            })
          }
          hint="Bounded, never infinite — an endlessly-retried dial is a wedged interface."
        />
        <PeerNumberInput
          label="Retry backoff (seconds)"
          value={connection.retry?.backoff_seconds ?? 1}
          min={0.1}
          max={3600}
          step="any"
          testId={`provider-connection-${index}-retry-backoff`}
          onChange={(backoff_seconds) =>
            set({ retry: { max_attempts: connection.retry?.max_attempts ?? 3, backoff_seconds } })
          }
        />
      </div>

      {/* GAP-58: both fields are editable, persisted and read by NOTHING — the
          `dial_out` half of `retry` has no transport (REQ-PROVIDER-008 refuses the
          role at assembly) and a failed `listen` bind is already retried by
          reconcile's next pass; `timeout_seconds` is superseded by the SAPIENT
          handshake's own 5 s bound. Architect ruling 2026-09-28 kept them
          expressible rather than retiring them, which only works if the form says
          so — an operator who types a budget here has no other way to learn it is
          inert. Not keyed to the role: it is true under BOTH. */}
      <p
        role="note"
        data-testid={`provider-connection-${index}-carried-not-honoured`}
        className="mt-2 rounded border border-warning px-2 py-1 text-meta text-warning"
      >
        No ingress transport reads the timeout or retry values yet, so changing them has no effect
        at runtime. They are saved and inspected like any other field.
      </p>

      {/* OQ-V-1: the role is model-present and refused at assembly. The editor
          offers it — a configuration an operator can persist should be one they
          can see — and labels it rather than letting it fail silently at start. */}
      {role?.unrealizedNote !== null && role?.unrealizedNote !== undefined && (
        <p
          role="status"
          data-testid={`provider-connection-${index}-unrealized`}
          className="mt-2 rounded border border-warning px-2 py-1 text-meta text-warning"
        >
          {role.unrealizedNote}
        </p>
      )}
    </section>
  );
}
